import {
  type AtomCommandResult,
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@afar-research/client-runtime/state/runtime";
import { create } from "zustand";

import { stackedThreadToast, toastManager } from "../components/ui/toast";
import * as ThreadUndo from "./threadUndo";

export type ThreadUndoAction = "Settled" | "Snoozed" | "Archived";

interface UndoOptions {
  action: ThreadUndoAction;
  undo: () => Promise<AtomCommandResult<unknown, unknown>>;
  failureTitle: string;
  claim: ReturnType<typeof ThreadUndo.begin>;
}

interface UndoNotice {
  action: ThreadUndoAction;
  count: number;
  undo: () => Promise<void>;
}

export const useThreadUndoNotice = create<{ notice: UndoNotice | null }>(() => ({ notice: null }));

/**
 * The window in which a parked thread can still be brought back. Long enough to
 * notice the row leave and change your mind, short enough that the notice is
 * not still sitting there when you have moved on.
 */
const UNDO_WINDOW_MS = 5_000;

// Module state, not per-hook: consecutive actions of the same kind share one
// notice and are restored together, however many surfaces they came from.
let liveUndos: UndoOptions[] = [];
let expiry: ReturnType<typeof setTimeout> | undefined;

function refreshNotice() {
  liveUndos = liveUndos.filter(({ claim }) => claim.isCurrent());
  const latest = liveUndos.at(-1);
  if (!latest) {
    clearTimeout(expiry);
    useThreadUndoNotice.setState({ notice: null });
    return;
  }
  // The trailing run of same-kind actions: settling three threads in a row
  // offers one "Settled 3 threads", but a snooze after them starts over.
  const group: UndoOptions[] = [];
  for (let index = liveUndos.length - 1; index >= 0; index--) {
    const entry = liveUndos[index]!;
    if (entry.action !== latest.action) break;
    group.push(entry);
  }
  useThreadUndoNotice.setState({
    notice: {
      action: latest.action,
      count: group.length,
      undo: async () => {
        const current = group.filter(
          (entry) => liveUndos.includes(entry) && entry.claim.isCurrent(),
        );
        liveUndos = liveUndos.filter((entry) => !group.includes(entry));
        // Every claim is consumed before the first await, so a double click or
        // a held shortcut cannot restore the same group twice.
        for (const entry of current) entry.claim.finish();
        refreshNotice();
        await Promise.all(
          current.map(async ({ undo, failureTitle }) => {
            const reportFailure = (error: unknown) => {
              toastManager.add(
                stackedThreadToast({
                  type: "error",
                  title: failureTitle,
                  description: error instanceof Error ? error.message : "An error occurred.",
                }),
              );
            };
            try {
              const result = await undo();
              if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
                reportFailure(squashAtomCommandFailure(result));
              }
            } catch (error) {
              reportFailure(error);
            }
          }),
        );
      },
    },
  });
}

ThreadUndo.subscribe(refreshNotice);

/** Reverse the group the sidebar is currently offering; false when there is none. */
export function undoLatestThreadAction(): boolean {
  const notice = useThreadUndoNotice.getState().notice;
  if (!notice) return false;
  void notice.undo();
  return true;
}

/** Offer one compact confirmation covering every currently undoable action. */
export function showThreadUndoNotice(options: UndoOptions) {
  if (!options.claim.isCurrent()) return;
  liveUndos.push(options);
  refreshNotice();
  clearTimeout(expiry);
  expiry = setTimeout(() => {
    const expired = liveUndos;
    liveUndos = [];
    for (const { claim } of expired) claim.finish();
    refreshNotice();
  }, UNDO_WINDOW_MS);
}
